import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Headphones, Users } from "lucide-react";
import api from "@/lib/api";
import { DISCORD_URL } from "@/lib/images";
import { SectionLabel } from "@/components/bits";

const STATUS_DOT = {
  online: "bg-emerald-300",
  idle: "bg-amber-200",
  dnd: "bg-red-300",
};

function Avatar({ member, index }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.6, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.6 }}
      transition={{ delay: Math.min(index * 0.03, 0.9), duration: 0.35 }}
      className="relative group"
      title={member.username}
      data-testid={`cord-member-${member.id}`}
    >
      <img src={member.avatar_url} alt={member.username}
        className="h-10 w-10 sm:h-11 sm:w-11 rounded-full border border-white/15 object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:border-white/50 transition-all duration-300" />
      <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-black ${STATUS_DOT[member.status] || "bg-white/40"}`} />
      {member.game && (
        <span className="absolute -top-1.5 -left-1.5 h-2 w-2 bg-white animate-pulse" title={`Playing ${member.game}`} />
      )}
    </motion.div>
  );
}

export default function LiveCordWall() {
  const [live, setLive] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/discord/live").then((r) => alive && setLive(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const members = live?.members || [];
  const inGame = members.filter((m) => m.game);
  const staff = live?.staff || [];
  const staffOnline = staff.filter((s) => s.online);

  return (
    <section className="border-t border-white/10" data-testid="live-cord-wall">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionLabel>LIVE FROM THE CORD</SectionLabel>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl uppercase leading-[0.95]">
              Packed room,<br />right now
            </h2>
          </div>
          <div className="flex gap-px rounded-2xl overflow-hidden bg-white/10 border border-white/10 font-mono text-center">
            <div className="bg-black px-6 py-4">
              <div className="font-display text-3xl" data-testid="cord-online-count">{live?.online_count ?? "—"}</div>
              <div className="text-[9px] tracking-[0.2em] text-white/40 mt-1">ONLINE NOW</div>
            </div>
            <div className="bg-black px-6 py-4">
              <div className="font-display text-3xl" data-testid="cord-member-count">{live?.member_count ?? "—"}</div>
              <div className="text-[9px] tracking-[0.2em] text-white/40 mt-1">MEMBERS</div>
            </div>
            <div className="bg-black px-6 py-4">
              <div className="font-display text-3xl" data-testid="cord-ingame-count">{inGame.length}</div>
              <div className="text-[9px] tracking-[0.2em] text-white/40 mt-1">IN GAME</div>
            </div>
          </div>
        </div>

        {staff.length > 0 && (
          <div className="mt-10 rounded-2xl border border-white/15 bg-white/[0.03] px-5 py-4" data-testid="staff-on-duty">
            <div className="font-mono text-[9px] tracking-[0.25em] text-white/40">
              {staffOnline.length > 0 ? "// STAFF ON DUTY — REPLIES IN MINUTES" : "// STAFF — ALL OFFLINE RIGHT NOW"}
            </div>
            <div className="mt-3 flex flex-wrap gap-4">
              {staff.map((s) => (
                <div key={s.id || s.name} className={`flex items-center gap-2.5 ${s.online ? "" : "opacity-40 grayscale"}`} data-testid={`staff-${s.name}`}>
                  <span className="relative">
                    {s.avatar_url
                      ? <img src={s.avatar_url} alt={s.name} className="h-8 w-8 rounded-full border border-white/20 object-cover" />
                      : <span className="h-8 w-8 rounded-full border border-white/20 bg-white/10 flex items-center justify-center font-mono text-[10px] text-white/60">{(s.name || "S")[0].toUpperCase()}</span>}
                    <span className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border-2 border-black ${s.online ? (STATUS_DOT[s.status] || "bg-white/40") : "bg-white/20"}`} />
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.15em] text-white/80 uppercase">{s.name}</span>
                  {s.online
                    ? (s.game
                        ? <span className="font-mono text-[9px] tracking-[0.1em] text-white/35 uppercase">· {s.game}</span>
                        : <span className="font-mono text-[9px] tracking-[0.1em] text-emerald-200/70 uppercase">· ONLINE</span>)
                    : <span className="font-mono text-[9px] tracking-[0.1em] text-white/30 uppercase">· OFFLINE</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {live?.widget_enabled && members.length > 0 ? (
          <div className="mt-10">
            <AnimatePresence>
              <div className="flex flex-wrap gap-2.5">
                {members.map((m, i) => <Avatar key={m.id} member={m} index={i} />)}
              </div>
            </AnimatePresence>
            {inGame.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid="cord-ingame-chips">
                {inGame.slice(0, 8).map((m) => (
                  <span key={m.id} className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1.5 font-mono text-[9px] tracking-[0.15em] text-white/50">
                    <Headphones size={10} /> {m.username} · {String(m.game).toUpperCase()}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : live && (
          <p className="mt-10 font-mono text-[10px] tracking-[0.2em] text-white/35" data-testid="cord-widget-off">
            // {live.online_count ?? "?"} MEMBERS ONLINE — AVATAR WALL WAKES UP WHEN THE SERVER WIDGET IS ENABLED
          </p>
        )}

        <a href={DISCORD_URL} target="_blank" rel="noreferrer" data-testid="cord-join-btn"
          className="btn-inj inline-flex items-center gap-2 mt-10 px-8 py-4 text-xs">
          <Users size={15} /> JOIN THE CORD
        </a>
      </div>
    </section>
  );
}
